import React, { useState, useEffect } from "react";
import { useTheme } from "@mui/material/styles";
import { ExcelData } from "./ExcelData";
import { HandlePdfUpload } from "./handlePdfUpload";

interface ExtractedDataTableProps {
  excelData: ExcelData;
}

const ExtractedDataTable: React.FC<ExtractedDataTableProps> = ({ excelData }) => {
  if (!excelData) return null;

  const theme = useTheme();

  // Helper function to format date properly without timezone issues
  const formatDate = (date: Date | string): string => {
    if (!date) return '';
    
    // If it's already a Date object, format it directly
    if (date instanceof Date) {
      return date.toLocaleDateString("it-IT");
    }
    
    // If it's a string, check if it's in YYYY-MM-DD format
    const stringValue = String(date);
    if (stringValue.match(/^\d{4}-\d{2}-\d{2}$/)) {
      // Parse YYYY-MM-DD format manually to avoid timezone issues
      const [year, month, day] = stringValue.split('-').map(Number);
      const dateObj = new Date(year, month - 1, day); // month is 0-based
      return dateObj.toLocaleDateString("it-IT");
    }
    
    // Fallback: clean and convert for other formats
    const cleanValue = stringValue.replace('Z', '').replace(/T.*$/, '');
    const dateObj = new Date(cleanValue);
    return dateObj.toLocaleDateString("it-IT");
  };

  // MOBILE DEVICE SECTION ------------------
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768); // Detect Mobile devices size

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768); // Update state on window resize
    };

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  // PDF SECTION ----------------------------
  const savedPdfNames = React.useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("nanesa_saved_pdf_files") || "{}");
    } catch {
      return {};
    }
  }, []);

  function PdfCell({ value, type, code, savedPdfNames }: { value: string; type: "coa" | "ddt" | "sds"; code: string; savedPdfNames: any }) {
    if (value !== "N/A" && savedPdfNames[type]) {
      return (
        <a
          href="#"
          style={{ color: "#1976d2", cursor: "pointer" }}
          onClick={(e) => {
            e.preventDefault();
            HandlePdfUpload(code);
          }}
        >
          {value}
        </a>
      );
    }
    return <>{value}</>;
  }

  if (isMobile) {
    // Render vertically for mobile
    return (
      <div className="table-responsive" style={{ padding: "20px", textAlign: "center" }}>
        <table
          className="table table-bordered"
          style={{
            width: "100%",
            margin: "0 auto",
            backgroundColor: theme.palette.mode === "dark" ? "rgb(18,18,18)" : "rgb(255,255,255)",
            color: theme.palette.mode === "dark" ? "rgb(255,255,255)" : "rgb(0,0,0)",
            borderCollapse: "collapse",
          }}
        >
          <thead>
            <tr>
              <th
                colSpan={2}
                style={{
                  textAlign: "center",
                  backgroundColor: "rgb(190,209,113)",
                  color: "black",
                  fontSize: "1.2rem",
                  padding: "10px",
                }}
              >
                NANESA SRL
              </th>
            </tr>
            <tr>
              <th
                colSpan={2}
                style={{
                  textAlign: "center",
                  fontWeight: "bold",
                  fontSize: "1rem",
                  padding: "8px",
                }}
              >
                Prodotto fornito: {excelData.articolo}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              style={{
                backgroundColor: theme.palette.mode === "dark" ? "rgb(28,28,28)" : "rgb(245,245,245)",
              }}
            >
              <th style={{ fontWeight: "bold", padding: "8px" }}>Lotto</th>
              <td style={{ padding: "8px" }}>{excelData.lotto}</td>
            </tr>
            <tr>
              <th style={{ fontWeight: "bold", padding: "8px" }}>Data di produzione</th>
              <td style={{ padding: "8px" }}>{formatDate(excelData.dataProduzione)}</td>
            </tr>
            <tr
              style={{
                backgroundColor: theme.palette.mode === "dark" ? "rgb(28,28,28)" : "rgb(245,245,245)",
              }}
            >
              <th style={{ fontWeight: "bold", padding: "8px" }}>Quantità</th>
              <td style={{ padding: "8px" }}>{excelData.quantita}</td>
            </tr>
            <tr>
              <th style={{ fontWeight: "bold", padding: "8px" }}>CoA n.</th>
              <td style={{ padding: "8px" }}>
                {excelData.coaPdfId ? (
                  <a
                    href="#"
                    style={{ color: "#1976d2", cursor: "pointer" }}
                    onClick={(e) => {
                      e.preventDefault();
                      HandlePdfUpload(excelData.coaPdfId!);
                    }}
                  >
                    {excelData.coa}
                  </a>
                ) : (
                  excelData.coa
                )}
              </td>
            </tr>
            <tr
              style={{
                backgroundColor: theme.palette.mode === "dark" ? "rgb(28,28,28)" : "rgb(245,245,245)",
              }}
            >
              <th style={{ fontWeight: "bold", padding: "8px" }}>Ordine n.</th>
              <td style={{ padding: "8px" }}>{excelData.numeroOrdine}</td>
            </tr>
            <tr>
              <th style={{ fontWeight: "bold", padding: "8px" }}>N. DDT</th>
              <td style={{ padding: "8px" }}>
                {excelData.ddtPdfId ? (
                  <a
                    href="#"
                    style={{ color: "#1976d2", cursor: "pointer" }}
                    onClick={(e) => {
                      e.preventDefault();
                      HandlePdfUpload(excelData.ddtPdfId!);
                    }}
                  >
                    {excelData.numeroDdt}
                  </a>
                ) : (
                  excelData.numeroDdt
                )}
              </td>
            </tr>
            <tr
              style={{
                backgroundColor: theme.palette.mode === "dark" ? "rgb(28,28,28)" : "rgb(245,245,245)",
              }}
            >
              <th style={{ fontWeight: "bold", padding: "8px" }}>Cliente</th>
              <td style={{ padding: "8px" }}>{excelData.cliente}</td>
            </tr>
            <tr>
              <th style={{ fontWeight: "bold", padding: "8px" }}>Scheda di sicurezza</th>
              <td style={{ padding: "8px" }}>
                {excelData.sdsPdfId ? (
                  <a
                    href="#"
                    style={{ color: "#1976d2", cursor: "pointer" }}
                    onClick={(e) => {
                      e.preventDefault();
                      HandlePdfUpload(excelData.sdsPdfId!);
                    }}
                  >
                    SDS
                  </a>
                ) : excelData.sds === "SDS" ? (
                  "SDS"
                ) : (
                  "N/A"
                )}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    );
  }

  // Render horizontally for desktop
  return (
    <div style={{ width: "auto", padding: "20px" }}>
      <table
        style={{
          borderCollapse: "collapse",
          width: "70vw",
          margin: "0 auto",
          textAlign: "center",
          boxShadow: "0 0 10px rgba(0, 0, 0, 0.15)",
        }}
      >
        <thead>
          <tr>
            <th
              colSpan={8}
              style={{
                padding: "12px",
                border: "2px solid rgb(190,209,113)",
                fontWeight: "bolder",
                backgroundColor: theme.palette.mode === "dark" ? "rgb(190,209,113)" : "rgb(190,209,113)",
                color: "black",
                fontSize: "1.2rem",
              }}
            >
              NANESA SRL
            </th>
          </tr>
          <tr>
            <th
              colSpan={8}
              style={{
                padding: "10px",
                border: "2px solid rgb(190,209,113)",
                fontWeight: "bolder",
              }}
            >
              Prodotto fornito: {excelData.articolo}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            style={{
              backgroundColor: theme.palette.mode === "dark" ? "rgb(28,28,28)" : "rgb(245,245,245)",
            }}
          >
            <td
              style={{
                padding: "10px",
                borderLeft: "2px solid rgb(190,209,113)",
                fontWeight: "bolder",
              }}
            >
              Lotto
            </td>
            <td style={{ padding: "10px", fontWeight: "bolder" }}>Data di produzione</td>
            <td style={{ padding: "10px", fontWeight: "bolder" }}>Quantità</td>
            <td style={{ padding: "10px", fontWeight: "bolder" }}>CoA n.</td>
            <td style={{ padding: "10px", fontWeight: "bolder" }}>Ordine n.</td>
            <td style={{ padding: "10px", fontWeight: "bolder" }}>N. DDT</td>
            <td style={{ padding: "10px", fontWeight: "bolder" }}>Cliente</td>
            <td
              style={{
                padding: "10px",
                fontWeight: "bolder",
                borderRight: "2px solid rgb(190,209,113)",
              }}
            >
              Scheda di sicurezza
            </td>
          </tr>
          <tr>
            <td
              style={{
                padding: "10px",
                borderLeft: "2px solid rgb(190,209,113)",
                borderBottom: "2px solid rgb(190,209,113)",
                fontWeight: "normal",
              }}
            >
              {excelData.lotto}
            </td>
            <td
              style={{
                padding: "10px",
                borderBottom: "2px solid rgb(190,209,113)",
                fontWeight: "normal",
              }}
            >
              {formatDate(excelData.dataProduzione)}
            </td>
            <td
              style={{
                padding: "10px",
                borderBottom: "2px solid rgb(190,209,113)",
                fontWeight: "normal",
              }}
            >
              {excelData.quantita}
            </td>
            <td
              style={{
                padding: "10px",
                borderBottom: theme.palette.mode === "dark" ? "2px solid rgb(190,209,113)" : "2px solid rgb(190,209,113)",
                fontWeight: "normal",
              }}
            >
              {excelData.coaPdfId ? (
                <a
                  href="#"
                  style={{ color: "#1976d2", cursor: "pointer" }}
                  onClick={(e) => {
                    e.preventDefault();
                    HandlePdfUpload(excelData.coaPdfId!);
                  }}
                >
                  {excelData.coa}
                </a>
              ) : (
                excelData.coa
              )}
            </td>
            <td
              style={{
                padding: "10px",
                borderBottom: "2px solid rgb(190,209,113)",
                fontWeight: "normal",
              }}
            >
              {excelData.numeroOrdine}
            </td>
            <td
              style={{
                padding: "10px",
                borderBottom: theme.palette.mode === "dark" ? "2px solid rgb(190,209,113)" : "2px solid rgb(190,209,113)",
                fontWeight: "normal",
              }}
            >
              {excelData.ddtPdfId ? (
                <a
                  href="#"
                  style={{ color: "#1976d2", cursor: "pointer" }}
                  onClick={(e) => {
                    e.preventDefault();
                    HandlePdfUpload(excelData.ddtPdfId!);
                  }}
                >
                  {excelData.numeroDdt}
                </a>
              ) : (
                excelData.numeroDdt
              )}
            </td>
            <td
              style={{
                padding: "10px",
                borderBottom: "2px solid rgb(190,209,113)",
                fontWeight: "normal",
              }}
            >
              <span style={{ fontWeight: "normal" }}>{excelData.cliente}</span>
            </td>
            <td
              style={{
                padding: "10px",
                borderBottom: "2px solid rgb(190,209,113)",
                borderRight: "2px solid rgb(190,209,113)",
                fontWeight: "normal",
              }}
            >
              {excelData.sdsPdfId ? (
                <a
                  href="#"
                  style={{ color: "#1976d2", cursor: "pointer" }}
                  onClick={(e) => {
                    e.preventDefault();
                    HandlePdfUpload(excelData.sdsPdfId!);
                  }}
                >
                  SDS
                </a>
              ) : excelData.sds === "SDS" ? (
                "SDS"
              ) : (
                "N/A"
              )}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
};

export default ExtractedDataTable;
